// Framework-independent loader/runtime so loading failures and rapid language
// switches can be tested without depending on the browser's chunk cache.
export function createTranslationLoader(importTranslation) {
  const pending = new Map();
  return language => {
    if (!pending.has(language)) {
      const request = Promise.resolve().then(() => importTranslation(language))
        .then(module => module.default || module)
        .catch(error => { pending.delete(language); throw error; });
      pending.set(language, request);
    }
    return pending.get(language);
  };
}

export function createLocaleRuntime({ instance, loadTranslation, registry, onApplied = () => {} }) {
  let initialization;
  let requested = 0;
  let committing = Promise.resolve();

  const resourcesFor = async language => {
    const entries = await Promise.all(registry.fallbackChain(language, 'ui').map(async code =>
      [code, { translation: await loadTranslation(code) }]));
    return Object.fromEntries(entries);
  };

  const initialize = language => {
    if (!initialization) {
      const locale = registry.resolve(language, 'ui');
      initialization = resourcesFor(locale).then(async resources => {
        await instance.init({
          resources,
          lng: locale,
          fallbackLng: code => registry.fallbackChain(code, 'ui').filter(fallback => fallback !== code),
          supportedLngs: registry.list('ui').map(entry => entry.code),
          load: 'currentOnly',
          interpolation: { escapeValue: false },
          initImmediate: false,
        });
        onApplied(locale);
        return locale;
      }).catch(error => { initialization = undefined; throw error; });
    }
    return initialization;
  };

  const change = async (language, { signal } = {}) => {
    const request = ++requested;
    const locale = registry.match(language, 'ui');
    if (!locale) throw new Error('Unsupported interface language');
    await initialize(locale);
    const resources = await resourcesFor(locale);
    // Keep the currently displayed locale intact if a chunk fails, and do not
    // let a slow earlier request overwrite the user's most recent selection.
    const commit = committing.then(async () => {
      if (request !== requested || signal?.aborted) return null;
      Object.entries(resources).forEach(([code, namespaces]) => {
        instance.addResourceBundle(code, 'translation', namespaces.translation, true, true);
      });
      await instance.changeLanguage(locale);
      if (request !== requested || signal?.aborted) return null;
      onApplied(locale);
      return locale;
    });
    committing = commit.catch(() => {});
    return commit;
  };

  return { initialize, change };
}
